import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import { SI_PORTAL_BASE_PATH } from '../../siPortal.definitions';
import { categoriaPublicacao, millis } from '../publicacao.definitions';
import { usePublicacaoQuery, useRegistrosPublicacaoQuery } from '../usePublicacoes';

type Situacao = 'confirmou' | 'leu' | 'nao_abriu';

interface Linha {
  id: string;
  nome: string;
  email: string;
  situacao: Situacao;
  em: number | null;
}

const TEXTO: Record<Situacao, string> = { confirmou: 'Confirmou', leu: 'Leu, não confirmou', nao_abriu: 'Não abriu' };
const COR: Record<Situacao, string> = { confirmou: 'text-green-700', leu: 'text-amber-700', nao_abriu: 'text-rose-700' };

function baixarCsv(nomeArquivo: string, linhas: Linha[]) {
  const escapar = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const corpo = [
    ['Nome', 'E-mail', 'Situação', 'Data'].map(escapar).join(';'),
    ...linhas.map((l) =>
      [l.nome, l.email, TEXTO[l.situacao], l.em ? new Date(l.em).toLocaleString('pt-BR') : ''].map(escapar).join(';')
    ),
  ].join('\n');
  const blob = new Blob(['﻿' + corpo], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

/** Relatório de ciência (rota /si/publicacoes/:id/ciencia): quem confirmou (na versão atual) e quem falta. */
export default function CienciaRelatorioPage() {
  const { id } = useParams<{ id: string }>();
  const { data: publicacao, isLoading } = usePublicacaoQuery(id);
  const { data: ciencias = [] } = useRegistrosPublicacaoQuery(id, 'ciencias');
  const { data: leituras = [] } = useRegistrosPublicacaoQuery(id, 'leituras');
  const { data: ativos = [] } = useCollaboratorsPublicQuery();
  const [aba, setAba] = useState<'faltam' | 'confirmaram'>('faltam');

  const linhas = useMemo<Linha[]>(() => {
    if (!publicacao) return [];
    const versao = publicacao.ciencia.versao;
    const porEmail = (lista: typeof ciencias) => new Map(lista.map((r) => [r.email.toLowerCase(), r]));
    const cienciaDe = porEmail(ciencias.filter((c) => (c.versao ?? 1) >= versao));
    const leituraDe = porEmail(leituras);
    const cobrados =
      publicacao.ciencia.publico === 'todos'
        ? ativos
        : publicacao.ciencia.alvoIds.map(
            (alvo) => ativos.find((c) => c.id === alvo) ?? { id: alvo, name: alvo, email: '' }
          );
    return cobrados
      .map((c) => {
        const email = (c.email ?? '').toLowerCase();
        const ciencia = cienciaDe.get(email);
        const leitura = leituraDe.get(email);
        const situacao: Situacao = ciencia ? 'confirmou' : leitura ? 'leu' : 'nao_abriu';
        return { id: c.id, nome: c.name, email: c.email ?? '', situacao, em: millis((ciencia ?? leitura)?.em) };
      })
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [publicacao, ciencias, leituras, ativos]);

  const voltar = (
    <Link to={`${SI_PORTAL_BASE_PATH}/publicacoes`} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700">
      <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Voltar para Publicações
    </Link>
  );
  if (isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-raro-gray-100" />;
  if (!publicacao) return <div className="space-y-4">{voltar}<p className="text-sm text-raro-gray-500">Publicação não encontrada.</p></div>;

  const contar = (s: Situacao) => linhas.filter((l) => l.situacao === s).length;
  const faltam = linhas.filter((l) => l.situacao !== 'confirmou');
  const confirmaram = linhas.filter((l) => l.situacao === 'confirmou');
  const exibidas = aba === 'faltam' ? faltam : confirmaram;
  const prazo = millis(publicacao.ciencia.prazo);
  const categoria = categoriaPublicacao(publicacao.categoria);

  return (
    <div className="space-y-4">
      {voltar}
      <div className="rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm">
        <p className={`text-xs font-semibold ${categoria.texto}`}>
          {categoria.label} · {prazo ? `ciência até ${new Date(prazo).toLocaleDateString('pt-BR')}` : 'ciência sem prazo'}
          {publicacao.ciencia.versao > 1 && ` · ${publicacao.ciencia.versao}ª ciência pedida`}
        </p>
        <h2 className="mt-1 font-montserrat text-xl font-bold text-raro-gray-900">{publicacao.titulo}</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl bg-raro-gray-50 p-3">
            <p className="text-xs text-raro-gray-500">{publicacao.ciencia.publico === 'todos' ? 'Colaboradores ativos' : 'Cobrados'}</p>
            <p className="text-xl font-bold">{linhas.length}</p>
          </div>
          <div className="rounded-xl bg-green-50 p-3">
            <p className="text-xs text-green-700">Confirmaram</p>
            <p className="text-xl font-bold text-green-800">{contar('confirmou')}</p>
          </div>
          <div className="rounded-xl bg-amber-50 p-3">
            <p className="text-xs text-amber-700">Leram, sem confirmar</p>
            <p className="text-xl font-bold text-amber-800">{contar('leu')}</p>
          </div>
          <div className="rounded-xl bg-rose-50 p-3">
            <p className="text-xs text-rose-700">Não abriram</p>
            <p className="text-xl font-bold text-rose-800">{contar('nao_abriu')}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 text-sm">
          {(
            [
              { valor: 'faltam', label: `Faltam (${faltam.length})` },
              { valor: 'confirmaram', label: `Confirmaram (${confirmaram.length})` },
            ] as const
          ).map((opcao) => (
            <button
              key={opcao.valor}
              type="button"
              onClick={() => setAba(opcao.valor)}
              className={clsx(
                'rounded-full px-4 py-2 font-montserrat',
                aba === opcao.valor ? 'bg-primary-50 font-semibold text-primary-700 ring-1 ring-primary-200' : 'text-raro-gray-600'
              )}
            >
              {opcao.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={() => baixarCsv(`ciencia-${publicacao.titulo.slice(0, 40)}.csv`, linhas)}
        >
          Exportar lista (CSV)
        </button>
      </div>

      {exibidas.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-raro-gray-300 bg-white px-4 py-6 text-center text-sm text-raro-gray-500">
          {aba === 'faltam' ? 'Todo mundo já confirmou. 🎉' : 'Ninguém confirmou ainda.'}
        </p>
      ) : (
        <ul className="divide-y divide-raro-gray-100 overflow-hidden rounded-2xl border border-raro-gray-200 bg-white text-sm shadow-sm">
          {exibidas.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <span className="min-w-0 truncate">
                {l.nome} <span className="text-xs text-raro-gray-400">· {l.email}</span>
              </span>
              <span className={`shrink-0 text-xs ${COR[l.situacao]}`}>
                {TEXTO[l.situacao]}
                {l.em ? ` em ${new Date(l.em).toLocaleDateString('pt-BR')}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
